import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { errorColorFor } from '../shared/errorColor'

const tokens = {
  dark: {
    bg: 'bg.input',
    border: 'border.search',
    borderHover: 'border.input-hover',
    focusRing: 'interactive.focus-ring',
    icon: 'icon.secondary',
    stepperHover: 'interactive.tertiary-hover-bg',
    text: 'text.primary',
  },
  light: {
    bg: 'light.bg.input',
    border: 'light.border.search',
    borderHover: 'light.border.input-hover',
    focusRing: 'light.interactive.focus-ring',
    icon: 'light.icon.secondary',
    stepperHover: 'light.interactive.tertiary-hover-bg',
    text: 'light.text.primary',
  },
} as const

export const numberInputContainerStyles = (
  colorScheme: ColorScheme,
  disabled: boolean,
  width: string | number,
  hasWarning = false
): SystemStyleObject => {
  const t = tokens[colorScheme]
  const borderColor = hasWarning ? errorColorFor(colorScheme) : t.border

  return {
    bg: t.bg,
    border: '1px solid',
    borderColor,
    display: 'flex',
    height: '36px',
    opacity: disabled ? 0.5 : 1,
    overflow: 'hidden',
    transition: 'border-color 0.15s ease',
    width,
    _focusWithin: { borderColor: hasWarning ? borderColor : t.focusRing },
    _hover: disabled
      ? undefined
      : { borderColor: hasWarning ? borderColor : t.borderHover },
  }
}

export const numberInputWarningStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: errorColorFor(colorScheme),
  fontFamily: "'Soehne Kraftig', sans-serif",
  fontSize: '8px',
  fontWeight: '500',
  letterSpacing: '0.08em',
  lineHeight: 1.2,
  pt: '4px',
  textTransform: 'uppercase',
})

export const numberInputFieldStyles = (
  colorScheme: ColorScheme,
  disabled: boolean
): SystemStyleObject => ({
  appearance: 'textfield',
  bg: 'transparent',
  border: 'none',
  color: tokens[colorScheme].text,
  cursor: disabled ? 'not-allowed' : undefined,
  flex: 1,
  fontFamily: 'Soehne',
  fontSize: '11px',
  minWidth: 0,
  outline: 'none',
  px: '10px',
  py: 0,
  '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
    appearance: 'none',
    margin: 0,
  },
})

export const numberInputStepperStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  borderLeft: '1px solid',
  borderLeftColor: tokens[colorScheme].border,
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  width: '28px',
})

export const numberInputStepperButtonStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => {
  const t = tokens[colorScheme]

  return {
    alignItems: 'center',
    appearance: 'none',
    bg: 'transparent',
    border: 0,
    color: t.icon,
    cursor: 'pointer',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    minHeight: 0,
    p: 0,
    _disabled: {
      cursor: 'not-allowed',
      opacity: 0.35,
    },
    _hover: {
      bg: t.stepperHover,
    },
  }
}
